// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
'use client';
import React from 'react';
import { getAnalyticsMetadataAttribute } from '@cloudscape-design/component-toolkit/internal/analytics-metadata';
import { AnalyticsFunnelSubStep } from '../internal/analytics/components/analytics-funnel';
import { getAnalyticsMetadataProps } from '../internal/base-component';
import { CollectionPreferencesMetadata } from '../internal/context/collection-preferences-metadata-context';
import useBaseComponent from '../internal/hooks/use-base-component';
import { applyDisplayName } from '../internal/utils/apply-display-name';
import { getColumnGroupsDepth } from './column-groups/utils';
import { getSortingColumnId } from './header-cell/utils';
import InternalTable, { InternalTableAsSubstep } from './internal';
const Table = React.forwardRef(({ items = [], selectedItems = [], variant = 'container', contentDensity = 'comfortable', cellVerticalAlign = 'middle', firstIndex = 1, ...props }, ref) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const analyticsMetadata = getAnalyticsMetadataProps(props);
    const hasHiddenColumns = (props.visibleColumns && props.visibleColumns.length < props.columnDefinitions.length) ||
        ((_a = props.columnDisplay) === null || _a === void 0 ? void 0 : _a.some(col => col.type !== 'group' && !col.visible));
    const hasStickyColumns = !!((_b = props.stickyColumns) === null || _b === void 0 ? void 0 : _b.first) || !!((_c = props.stickyColumns) === null || _c === void 0 ? void 0 : _c.last);
    const baseComponentProps = useBaseComponent('Table', {
        props: {
            contentDensity,
            resizableColumns: props.resizableColumns,
            selectionType: props.selectionType,
            stickyHeader: props.stickyHeader,
            stripedRows: props.stripedRows,
            variant,
            wrapLines: props.wrapLines,
            enableKeyboardNavigation: props.enableKeyboardNavigation,
            totalItemsCount: props.totalItemsCount,
            flowType: analyticsMetadata.flowType,
            cellVerticalAlign,
        },
        metadata: {
            expandableRows: !!props.expandableRows,
            progressiveLoading: !!props.getLoadingStatus,
            hasSkeleton: !!props.skeleton,
            skeletonTotalRows: typeof ((_d = props.skeleton) === null || _d === void 0 ? void 0 : _d.totalRows) === 'number' ? props.skeleton.totalRows : null,
            skeletonAutoRows: ((_e = props.skeleton) === null || _e === void 0 ? void 0 : _e.totalRows) === 'auto',
            skeletonMaxAutoRows: ((_f = props.skeleton) === null || _f === void 0 ? void 0 : _f.totalRows) === 'auto' ? ((_g = props.skeleton.maxAutoRows) !== null && _g !== void 0 ? _g : null) : null,
            skeletonMinAutoRows: ((_h = props.skeleton) === null || _h === void 0 ? void 0 : _h.totalRows) === 'auto' ? ((_j = props.skeleton.minAutoRows) !== null && _j !== void 0 ? _j : null) : null,
            groupSelection: !!((_k = props.expandableRows) === null || _k === void 0 ? void 0 : _k.groupSelection),
            columnGroups: !!((_l = props.groupDefinitions) === null || _l === void 0 ? void 0 : _l.length),
            columnGroupsDepth: getColumnGroupsDepth(props.columnDisplay),
            cellCounters: props.columnDefinitions.filter(dev => !!dev.counter).length,
            loaderCounters: !!props.renderLoaderCounter,
            inlineEdit: props.columnDefinitions.some(def => !!def.editConfig),
            disabledInlineEdit: props.columnDefinitions.some(def => { var _a; return !!((_a = def.editConfig) === null || _a === void 0 ? void 0 : _a.disabledReason); }),
            hasSortableColumns: props.columnDefinitions.some(def => def.sortingField || def.sortingComparator),
            hasMultiColumnSort: !!props.multiColumnSort,
            hasHiddenColumns,
            hasStickyColumns,
            hasFilterSlot: !!props.filter,
            hasPaginationSlot: !!props.pagination,
            itemsCount: items.length,
            hasInstanceIdentifier: Boolean(analyticsMetadata === null || analyticsMetadata === void 0 ? void 0 : analyticsMetadata.instanceIdentifier),
            hasResourceType: Boolean(analyticsMetadata === null || analyticsMetadata === void 0 ? void 0 : analyticsMetadata.resourceType),
            usesVisibleColumns: !!props.visibleColumns,
            usesColumnDisplay: !!props.columnDisplay,
            usesColumnDefinitionsVerticalAlign: props.columnDefinitions.some(def => def.verticalAlign !== cellVerticalAlign),
        },
    }, analyticsMetadata);
    const analyticsComponentMetadata = {
        name: 'awsui.Table',
        label: { root: 'self' },
        properties: {
            selectionType: props.selectionType || 'none',
            itemsCount: `${items.length}`,
            selectedItemsCount: `${selectedItems.length}`,
            variant,
        },
    };
    const sortingColumnId = getSortingColumnId(props.columnDefinitions, props.sortingColumn);
    if (sortingColumnId) {
        analyticsComponentMetadata.properties.sortingColumnId = sortingColumnId;
        analyticsComponentMetadata.properties.sortingDescending = `${props.sortingDescending || false}`;
    }
    const tableProps = {
        items,
        selectedItems,
        variant,
        contentDensity,
        firstIndex,
        cellVerticalAlign,
        ...props,
        ...baseComponentProps,
        ref,
        ...getAnalyticsMetadataAttribute({ component: analyticsComponentMetadata }),
    };
    const collectionPreferencesMetadata = {
        tableContentDensity: contentDensity,
        tableHasStripedRows: !!props.stripedRows,
        tableHasHiddenColumns: hasHiddenColumns,
        tableHasStickyColumns: hasStickyColumns,
    };
    if (variant === 'borderless' || variant === 'embedded') {
        return (React.createElement(CollectionPreferencesMetadata.Provider, { value: collectionPreferencesMetadata },
            React.createElement(InternalTable, { ...tableProps })));
    }
    return (React.createElement(CollectionPreferencesMetadata.Provider, { value: collectionPreferencesMetadata },
        React.createElement(AnalyticsFunnelSubStep, null,
            React.createElement(InternalTableAsSubstep, { ...tableProps }))));
});
applyDisplayName(Table, 'Table');
export default Table;
//# sourceMappingURL=index.js.map